// frontend/src/widgets/WidgetMigration.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-07-30
// Updated:      2026-07-30
//
// Description:  Transitional widget-id migration utilities for upgrading legacy stored layouts
//               to canonical WidgetRegistry ids without retaining aliases in normal runtime logic.
//
// Change Summary (2026-07-30):
//   - Centralised the six Opportunities aliases previously held inside OpportunitiesPage.jsx.
//   - Added migration from the legacy Dashboard id `weather_spot` to `weather_spot_widget`.
//   - Added list and layout migration helpers with deterministic de-duplication.
//   - Added a storage migration helper that silently self-heals valid localStorage records.
//
// Operational Notes:
//   - This module is temporary and must be deleted after all supported stored layouts have been
//     migrated and no page, database layout, or deployment still references a legacy id.
//   - Do not add new aliases as a substitute for correcting registry/database/page consistency.
//   - Canonical ids remain defined exclusively by WidgetRegistry.js.

const LEGACY_WIDGET_ID_MAP = Object.freeze({
  opp_stage_progress: "opportunity_stage_progress",
  opp_inbox: "opportunity_inbox",
  opp_list: "opportunity_list",
  opp_detail: "opportunity_detail",
  opp_financial_summary: "financial_summary_widget",
  opp_ai_insights: "opportunity_ai_insights",
  weather_spot: "weather_spot_widget",
});

export const migrateWidgetId = (widgetId) => {
  if (typeof widgetId !== "string") return widgetId;
  return LEGACY_WIDGET_ID_MAP[widgetId] || widgetId;
};

export const isLegacyWidgetId = (widgetId) =>
  typeof widgetId === "string" && Object.prototype.hasOwnProperty.call(LEGACY_WIDGET_ID_MAP, widgetId);

export const migrateWidgetIds = (widgetIds) => {
  if (!Array.isArray(widgetIds)) return [];
  return Array.from(
    new Set(
      widgetIds
        .filter((widgetId) => typeof widgetId === "string" && widgetId.trim() !== "")
        .map(migrateWidgetId)
    )
  );
};

export const migrateWidgetLayout = (layout) => {
  if (!Array.isArray(layout)) return [];

  const seen = new Set();
  const migrated = [];

  layout.forEach((item) => {
    if (!item || typeof item !== "object" || typeof item.i !== "string") return;

    const canonicalId = migrateWidgetId(item.i);
    if (seen.has(canonicalId)) return;

    seen.add(canonicalId);
    migrated.push(canonicalId === item.i ? item : { ...item, i: canonicalId });
  });

  return migrated;
};

export const migrateStoredWidgetState = ({ layoutKey, widgetsKey, storage = window.localStorage }) => {
  if (!layoutKey || !widgetsKey || !storage) {
    return { layout: null, widgetIds: null, migrated: false };
  }

  const rawLayout = JSON.parse(storage.getItem(layoutKey));
  const rawWidgetIds = JSON.parse(storage.getItem(widgetsKey));

  if (!Array.isArray(rawLayout) || !Array.isArray(rawWidgetIds)) {
    return { layout: null, widgetIds: null, migrated: false };
  }

  const layout = migrateWidgetLayout(rawLayout);
  const widgetIds = migrateWidgetIds(rawWidgetIds);
  const migrated =
    JSON.stringify(layout) !== JSON.stringify(rawLayout) ||
    JSON.stringify(widgetIds) !== JSON.stringify(rawWidgetIds);

  if (migrated) {
    storage.setItem(layoutKey, JSON.stringify(layout));
    storage.setItem(widgetsKey, JSON.stringify(widgetIds));
  }

  return { layout, widgetIds, migrated };
};

export const getLegacyWidgetIdMap = () => ({ ...LEGACY_WIDGET_ID_MAP });
